import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Button, Grid, Group, IconButton, Layout, Page, Tree } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useModal, useTree, usePopup, usePage } from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_APRV_RQST_PPUP_INFO,
    SG_APRV_PATH_RQST_LIST,
    SG_APRV_CSTM_USR_LIST,
    SG_APRV_USR_LIST,
    SG_CSTM_LIST,
    SF_CSTM_OFCR_SRCH,
} from "./service/AprvService";
import { LayoutGroup } from "framer-motion";

/*
 * Popup for Approval Request
 * !== 결재 요청 팝업 ==!
 */
export const AprvRqstUsrLst = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { getParams } = usePopup();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Customs Search !== 세관 검색 ==!
        cstmOfcrSrch: useForm({
            defaultSchema: SF_CSTM_OFCR_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),

        // Approval Request Popup Information Form !== 결재 요청 팝업 정보 form ==!
        aprvRqstPpupInfo: useForm({
            defaultSchema: SF_APRV_RQST_PPUP_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Customs list inquiry !== 세관 목록 조회 ==!
        cstmList: useGrid({
            defaultSchema: SG_CSTM_LIST,
        }),
        // When selecting a Customs Office, check the list of target approver List !== 세관 선택시 대상자 목록 조회 ==!
        aprvCstmUsrList: useGrid({
            defaultSchema: SG_APRV_CSTM_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // When selecting an Organization Chart, View the List of Target User List  !== 조직도 선택시 대상자 목록 조회 ==!
        getCstmList: useFetch({
            api: (data: any) => APIS.getCsmtList(),
            onSuccess: (data) => {
                const cstmList = data["cstmList"].content;
                tree.setData([
                    {
                        name: "Customs Office",
                        menuId: "menu_root",
                        menuPath: "ROOT",
                        children: handler.createTreeData(cstmList, [], 1, "menu_root"),
                    },
                ]);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // 조회가 실패하였습니다.
        }),

        // When selecting an Organization Chart, View the List of Target User List  !== 조직도 선택시 대상자 목록 조회 ==!
        getAprvTreeTrgtUsrList: useFetch({
            api: (page = grid.aprvCstmUsrList.page) =>
                APIS.getAprvTreeTrgtUsrList(form.cstmOfcrSrch.getValues(), page, grid.aprvCstmUsrList.size),
            enabled: comnUtils.isEmpty(form.aprvRqstPpupInfo.errors),
            key: [grid.aprvCstmUsrList.page, grid.aprvCstmUsrList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.aprvRqstPpupInfo.getValues(),
                    page: grid.aprvCstmUsrList.page,
                    size: grid.aprvCstmUsrList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Repacking Item Application List !== 세관 목록 조회 ==!
        getAprvTreeTrgtUsrList: () => {
            form.cstmOfcrSrch.handleSubmit(
                (data) => {
                    grid.aprvCstmUsrList.setPage(0);
                    fetch.getAprvTreeTrgtUsrList.fetch(0);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        createTreeData: (orgList: any, newList: any, level: any, menuId: any) => {
            const tempList = orgList.map((item: any) => {
                return {
                    name: comnUtils.replaceEmpty(item.cstmNm),
                    checkbox: false,
                    cstmOfceCd: comnUtils.replaceEmpty(item.cstmOfceCd),
                };
            });

            return tempList;
        },
        // Click Customs List !== 세관 클릭 ==!
        onTreeClick: (treeInfo: any) => {
            form.aprvRqstPpupInfo.setValue("cstmOfceCd", treeInfo.cstmOfceCd);
            form.cstmOfcrSrch.setValue("cstmOfceCd", treeInfo.cstmOfceCd);
            form.cstmOfcrSrch.setValue("cstmNm", treeInfo.name);
            fetch.getAprvTreeTrgtUsrList.fetch();
        },

        onGridBtnAdd: () => {
            const trgtUsrList = grid.aprvCstmUsrList.getChecked(); // Approver Target List !== 결재자 대상 선택 목록 ==!
            if (trgtUsrList.length === 0) {
                modal.openModal({ content: "msg.ptl.00687" }); // Please select a registration target !== 등록대상을 선택하세요. ==!
                return;
            }

            modal.postMessage({
                trgtUsrList: trgtUsrList,
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getCstmList.fetch();
        fetch.getAprvTreeTrgtUsrList.fetch();
        tree.tree._tree.current._checkbox = false;
    }, []);

    return (
        <Page title={t("Officer List")}>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.cstmOfcrSrch.schema.cstmNm}></Group.Control>
                                <Group.Control {...form.cstmOfcrSrch.schema.usrNm}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    onClick={() => {
                                        form.cstmOfcrSrch.reset();
                                    }}
                                >
                                    {t("B_RESET")}
                                </Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    onClick={() => {
                                        handler.getAprvTreeTrgtUsrList();
                                    }}
                                >
                                    {t("B_SRCH")}
                                </Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>
            <Group>
                <Group.Body>
                    <Layout direction={"row"}>
                        <Layout height={400}>
                            <Group>
                                <Group.Body>
                                    <Group.Title title={t("List of Organizations")} titleSize={3} />
                                    <Tree {...tree.tree} size={12} height={340} onClick={handler.onTreeClick} />
                                </Group.Body>
                            </Group>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Layout.Right>
                                            <Button role="gridAdd" onClick={handler.onGridBtnAdd} />
                                        </Layout.Right>
                                        <Layout direction="row"></Layout>
                                        <Grid
                                            {...grid.aprvCstmUsrList.grid}
                                            data={fetch.getAprvTreeTrgtUsrList.data?.aprvCstmUsrList}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Layout>
                    </Layout>
                </Group.Body>
            </Group>
        </Page>
    );
};
